import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    FinancialAccount,
    FinancialAccountSourceType,
    FinancialAccountType,
} from "@common/types/financialAccount";
import {
    useAccountDisablingMutation,
    useFinancialAccountRemovalMutation,
} from "@mutations/financialAccount";

import { StandardModal } from "@design-system/Modal/Modal";
import { getManualJournalEntries } from "@lib/accounting";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    AccountAction,
    RemoveAccountAttentionContent,
} from "./RemoveAccountAttentionContent";
import { RemoveAccountManualJournalsReviewContent } from "./RemoveAccountManualJournalsReviewContent";
import { RemoveAccountConfirmationContent } from "./RemoveAccountConfirmationContent";

interface Props {
    showModal: boolean;
    setShowModal: (showModal: boolean) => void;
    account: FinancialAccount;
    closeModal: () => void;
}

enum RemoveAccountConfirmationStep {
    Attention,
    ManualJournalsReview,
    Confirmation,
}

export const RemoveFinancialAccountModal: React.FC<Props> = ({
    showModal,
    setShowModal,
    account,
    closeModal,
}) => {
    const { toast } = useToaster();

    const removeAccount = useFinancialAccountRemovalMutation(account, () => {
        toast("Account removed");
        closeModal();
    });
    const disableAccount = useAccountDisablingMutation(account, () => {
        toast("Account archived. We won't bring new data for this account.");
        closeModal();
    });
    const manualJournalEntries = useQuery({
        queryKey: [
            "manualJournalEntries",
            account.entityId,
            account.id,
            "exclude-imported",
        ],
        queryFn: () =>
            getManualJournalEntries(account.entityId!, {
                financialAccountId: account.id,
                excludeImported: true,
            }),
        enabled:
            !!account.isBusiness &&
            account.financialType !== FinancialAccountType.CASH,
    });

    const [confirmationStep, setConfirmationStep] = useState(
        RemoveAccountConfirmationStep.Attention,
    );
    const [accountAction, setAccountAction] = useState<AccountAction>(
        "keep-historical-data",
    );

    const hasManualJournals =
        manualJournalEntries.data && manualJournalEntries.data.length > 0;

    const onRemoveAccount = useCallback(() => {
        if (manualJournalEntries.isLoading) {
            return;
        }
        if (hasManualJournals) {
            setConfirmationStep(
                RemoveAccountConfirmationStep.ManualJournalsReview,
            );
            return;
        }
        setConfirmationStep(RemoveAccountConfirmationStep.Confirmation);
    }, [manualJournalEntries.isLoading, hasManualJournals]);

    const removeAccountLoadingState = hasManualJournals
        ? false
        : removeAccount.isPending;

    const accountDoesntHaveConnection =
        account.sourceType === FinancialAccountSourceType.FILE_IMPORT;

    useEffect(() => {
        // NOTE: The difference with file imported accounts is that for them we don't need a first step
        // and automatically transition them to the next one in the state machine (MJs review)
        // At the same time, MJs review state will be automatically transitioned to the confirmation step
        // if there are no conflicting manual journals.
        if (showModal && accountDoesntHaveConnection) {
            setConfirmationStep(
                RemoveAccountConfirmationStep.ManualJournalsReview,
            );
        }
    }, [accountDoesntHaveConnection, showModal]);

    return (
        <StandardModal show={showModal} onHide={() => setShowModal(false)}>
            {showModal && (
                <>
                    {confirmationStep ===
                        RemoveAccountConfirmationStep.Attention && (
                        <RemoveAccountAttentionContent
                            account={account}
                            closeModal={() => setShowModal(false)}
                            disableAccount={disableAccount}
                            onRemoveAccount={onRemoveAccount}
                            removeAccountLoadingState={
                                removeAccountLoadingState
                            }
                            accountAction={accountAction}
                            onAccountActionChange={setAccountAction}
                        />
                    )}
                    {confirmationStep ===
                        RemoveAccountConfirmationStep.ManualJournalsReview && (
                        <RemoveAccountManualJournalsReviewContent
                            entityId={account.entityId!}
                            journalEntries={manualJournalEntries}
                            forward={() =>
                                setConfirmationStep(
                                    RemoveAccountConfirmationStep.Confirmation,
                                )
                            }
                            back={() =>
                                setConfirmationStep(
                                    RemoveAccountConfirmationStep.Attention,
                                )
                            }
                        />
                    )}

                    {confirmationStep ===
                        RemoveAccountConfirmationStep.Confirmation && (
                        <RemoveAccountConfirmationContent
                            account={account}
                            back={
                                accountDoesntHaveConnection
                                    ? undefined
                                    : () =>
                                          setConfirmationStep(
                                              RemoveAccountConfirmationStep.Attention,
                                          )
                            }
                            removeAccountMutation={removeAccount}
                            forceDeletion={hasManualJournals}
                        />
                    )}
                </>
            )}
        </StandardModal>
    );
};
